// import node module libraries
import { Fragment, useState } from "react";
import PropTypes from "prop-types";

import { useMutation } from "@apollo/client/react";
import gql from 'graphql-tag';

import {
  Col,
  Row,
  ProgressBar,
  Form,
  Button,
  Modal,
  Card,
  InputGroup,
} from "react-bootstrap";

// import custom components
import Ratings from "components/marketing/common/ratings/Ratings";

import { Rating } from "@smastrom/react-rating";
import "@smastrom/react-rating/style.css";
import { Notify } from "notiflix";
import { Avatar } from "components/elements/bootstrap/Avatar";
import moment from "moment";

// ----------------------------------------------------------------------

ReviewsTab.propTypes = {
  reviews: PropTypes.array,
};

const CREATE_REVIEW = gql`
  mutation CreateCourseReview(
    $courseId: String!
    $description: String!
    $userId: String!
    $rating: String
  ) {
    createCourseReview(
      courseId: $courseId
      description: $description
      userId: $userId
      rating: $rating
    ) {
      courseReviews {
        description
        rating
        user {
          firstName
          guid
          email
          lastName
        }
        id
      }
    }
  }
`;

// ----------------------------------------------------------------------

const getTotalRating = (reviews) => {
  if (!reviews || reviews.length === 0) return 0;
  let _total = 0;
  reviews.forEach((item) => {
    if (item.rating) {
      _total += parseFloat(item.rating);
    }
  });

  return (_total / reviews.length).toFixed(1);
};

const getRatingDistribution = (reviews) => {
  const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  
  reviews.forEach(review => {
    const rating = Math.round(parseFloat(review.rating || 0));
    if (rating >= 1 && rating <= 5) {
      distribution[rating]++;
    }
  });

  return distribution;
};

// ----------------------------------------------------------------------

export default function ReviewsTab({
  reviews,
  course,
  user,
  isAuthenticated,
  isEnrolled,
  updateReviews,
}) {
  const [showAddDialog, setShow] = useState(false);
  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const totalReview = getTotalRating(reviews);
  const distribution = getRatingDistribution(reviews);
  const totalReviews = reviews?.length || 0;

  const [onSaveReview, { loading: isSavingReview }] = useMutation(CREATE_REVIEW);

  const handleReviewAdd = () => {
    const payload = {
      description: description,
      rating: `${rating}`,
      userId: user?.guid,
      courseId: course?.guid,
    };

    onSaveReview({
      variables: payload,
      onCompleted: (data) => {
        Notify.success("Review added successfully!")
        setShow(false);
        setRating(0);
        setDescription("");
        updateReviews(data.createCourseReview.courseReviews);
      },
      onError: (err) => {
        Notify.failure("Failed to add review!")
      }
    });
  };

  const filteredReviews = reviews?.filter(review => {
    if (!searchTerm) return true;
    const fullName = `${review?.user?.firstName} ${review?.user?.lastName}`.toLowerCase();
    const desc = review?.description?.toLowerCase() || "";
    return fullName.includes(searchTerm.toLowerCase()) || desc.includes(searchTerm.toLowerCase());
  });

  return (
    <Fragment>
      {/* Rating Overview */}
      <div className="rating-overview-section mb-5">
        <div className="d-flex justify-content-between align-items-start mb-4">
          <div>
            <h3 className="section-title mb-2">Student Feedback</h3>
            <p className="text-muted mb-0">See what students are saying about this course</p>
          </div>
          {isAuthenticated && isEnrolled && (
            <Button 
              variant="primary" 
              onClick={() => setShow(true)}
              className="add-review-btn"
            >
              <i className="fe fe-edit-3 me-2"></i>
              Write a Review
            </Button>
          )}
        </div>

        <Card className="rating-card">
          <Card.Body className="p-4 p-lg-5">
            <Row className="align-items-center g-4">
              {/* Overall Rating */}
              <Col lg={4} md={12} className="text-center border-end-lg">
                <div className="overall-rating">
                  <div className="rating-number mb-2">
                    {!isNaN(totalReview) ? totalReview : "0.0"}
                  </div>
                  <div className="rating-stars mb-2">
                    <Ratings rating={totalReview} size="1.25rem" />
                  </div>
                  <p className="rating-text mb-0">
                    Based on <strong>{totalReviews}</strong> {totalReviews === 1 ? 'review' : 'reviews'}
                  </p>
                </div>
              </Col>

              {/* Rating Distribution */}
              <Col lg={8} md={12}>
                <div className="rating-distribution">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = distribution[star];
                    const percentage = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
                    
                    return (
                      <div key={star} className="rating-bar-item">
                        <div className="star-label">
                          <Ratings rating={star} size="0.875rem" />
                        </div>
                        <div className="progress-wrapper">
                          <ProgressBar 
                            now={percentage} 
                            className="rating-progress"
                          />
                        </div>
                        <div className="percentage-label">
                          {percentage.toFixed(0)}%
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>

      {/* Reviews List */}
      <div className="reviews-list-section">
        <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3 mb-4">
          <div>
            <h4 className="mb-1">All Reviews ({filteredReviews?.length || 0})</h4>
            <p className="text-muted mb-0">Real feedback from our students</p>
          </div>

          {/* Search */}
          <div className="search-wrapper">
            <InputGroup className="search-input-group">
              <InputGroup.Text className="search-icon">
                <i className="fe fe-search"></i>
              </InputGroup.Text>
              <Form.Control
                type="search"
                placeholder="Search reviews..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </InputGroup>
          </div>
        </div>

        {/* Review Items */}
        <div className="reviews-container">
          {filteredReviews?.length === 0 ? (
            <Card className="empty-state-card">
              <Card.Body className="text-center py-5">
                <i className="fe fe-message-square" style={{fontSize: "3rem", color: "#cbd5e1", marginBottom: "1rem"}}></i>
                <h5 className="mb-2">No reviews yet</h5>
                <p className="text-muted mb-0">Be the first to share your thoughts about this course!</p>
              </Card.Body>
            </Card>
          ) : (
            filteredReviews.map((item, index) => (
              <Card key={index} className="review-card">
                <Card.Body className="p-4">
                  <div className="d-flex gap-3">
                    <Avatar
                      size="lg"
                      className="rounded-circle review-avatar"
                      type="initial"
                      src={item?.user?.image}
                      alt={item?.user?.firstName}
                      name={`${item?.user?.firstName} ${item?.user?.lastName}`}
                    />
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h5 className="reviewer-name mb-1">
                            {`${item?.user?.firstName} ${item?.user?.lastName}`}
                          </h5>
                          <div className="review-meta">
                            <span className="review-rating">
                              <Ratings rating={item?.rating || 0} size="0.875rem" />
                            </span>
                            <span className="review-date">
                              {item?.postedon || moment(item?.createdAt).fromNow()}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div 
                        className="review-content"
                        dangerouslySetInnerHTML={{
                          __html: item?.description,
                        }}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Add Review Modal */}
      <Modal
        show={showAddDialog}
        onHide={() => setShow(false)}
        centered
        size="lg"
        className="review-modal"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <div>
            <h3 className="mb-1 fw-bold">Share Your Experience</h3>
            <p className="text-muted mb-0">Help others learn about this course</p>
          </div>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <Form>
            <Row className="g-4">
              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2">
                    <i className="fe fe-star me-2"></i>
                    Your Rating
                  </Form.Label>
                  <div className="rating-input-wrapper">
                    <Rating
                      id="rating"
                      items={5}
                      value={rating}
                      onChange={setRating}
                      style={{ maxWidth: 200 }}
                    />
                    {rating > 0 && (
                      <span className="rating-label ms-3">
                        {rating === 5 && "⭐ Excellent!"}
                        {rating === 4 && "👍 Great!"}
                        {rating === 3 && "😊 Good"}
                        {rating === 2 && "😐 Fair"}
                        {rating === 1 && "😞 Poor"}
                      </span>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold mb-2">
                    <i className="fe fe-message-square me-2"></i>
                    Your Review
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    placeholder="Share your thoughts about this course. What did you like? What could be improved?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="review-textarea"
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <div className="d-flex gap-2 justify-content-end">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setShow(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleReviewAdd}
                    disabled={isSavingReview || rating === 0 || !description.trim()}
                    className="submit-review-btn"
                  >
                    {isSavingReview ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <i className="fe fe-send me-2"></i>
                        Submit Review
                      </>
                    )}
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
      </Modal>

      <style jsx>{`
        /* Section Title */
        .section-title {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1e293b;
        }

        /* Add Review Button */
        .add-review-btn {
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          border-radius: 10px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
        }

        /* Rating Card */
        .rating-card {
          border: none;
          background: linear-gradient(135deg, rgba(246, 133, 33, 0.03) 0%, rgba(29, 158, 176, 0.03) 100%);
          border-radius: 16px;
        }

        /* Overall Rating */
        .overall-rating {
          padding: 1rem;
        }

        .rating-number {
          font-size: 4rem;
          font-weight: 800;
          color: #1e293b;
          line-height: 1;
        }

        .rating-text {
          font-size: 0.9375rem;
          color: #64748b;
        }

        /* Rating Distribution */
        .rating-distribution {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .rating-bar-item {
          display: grid;
          grid-template-columns: 80px 1fr 50px;
          align-items: center;
          gap: 1rem;
        }

        .star-label {
          display: flex;
          justify-content: flex-end;
        }

        .progress-wrapper {
          flex: 1;
        }

        .rating-progress {
          height: 8px;
          border-radius: 10px;
          background-color: #e2e8f0;
        }

        .rating-progress .progress-bar {
          background: linear-gradient(90deg, #F68521 0%, #1d9eb0 100%);
          border-radius: 10px;
        }

        .percentage-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #64748b;
        }

        /* Search */
        .search-wrapper {
          min-width: 300px;
        }

        .search-input-group {
          border-radius: 10px;
          overflow: hidden;
        }

        .search-icon {
          background: white;
          border: 2px solid #e2e8f0;
          border-right: none;
          color: #94a3b8;
        }

        .search-input {
          border: 2px solid #e2e8f0;
          border-left: none;
          padding: 0.75rem 1rem;
        }

        .search-input:focus {
          border-color: #F68521;
          box-shadow: none;
        }

        .search-icon {
          border-color: #F68521;
        }

        /* Reviews Container */
        .reviews-container {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Review Card */
        .review-card {
          border: none;
          background: white;
          border-radius: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
          transition: all 0.3s ease;
        }

        .review-card:hover {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .review-avatar {
          flex-shrink: 0;
        }

        .reviewer-name {
          font-size: 1.0625rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.25rem;
        }

        .review-meta {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .review-date {
          font-size: 0.8125rem;
          color: #94a3b8;
        }

        .review-content {
          margin-top: 0.75rem;
          font-size: 0.9375rem;
          line-height: 1.7;
          color: #475569;
        }

        /* Empty State */
        .empty-state-card {
          border: 2px dashed #e2e8f0;
          background: #f8fafc;
          border-radius: 16px;
        }

        /* Modal */
        .review-modal .modal-content {
          border-radius: 20px;
          border: none;
        }

        .rating-input-wrapper {
          display: flex;
          align-items: center;
        }

        .rating-label {
          font-size: 1.125rem;
          font-weight: 600;
          color: #F68521;
        }

        .review-textarea {
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          padding: 1rem;
          font-size: 0.9375rem;
          resize: none;
        }

        .review-textarea:focus {
          border-color: #F68521;
          box-shadow: 0 0 0 3px rgba(246, 133, 33, 0.1);
        }

        .submit-review-btn {
          padding: 0.75rem 1.5rem;
          font-weight: 600;
          border-radius: 10px;
          background: linear-gradient(135deg, #F68521 0%, #1d9eb0 100%);
          border: none;
        }

        /* Responsive */
        @media (max-width: 991px) {
          .border-end-lg {
            border-right: none !important;
            border-bottom: 1px solid #e2e8f0;
            padding-bottom: 2rem;
            margin-bottom: 1rem;
          }
        }

        @media (max-width: 768px) {
          .search-wrapper {
            min-width: 100%;
            width: 100%;
          }

          .rating-bar-item {
            grid-template-columns: 70px 1fr 45px;
            gap: 0.75rem;
          }

          .overall-rating .rating-number {
            font-size: 3rem;
          }
        }
      `}</style>
    </Fragment>
  );
}